import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import { AlimentacionProps } from '../../components/Alimentacion_Pesaje/Alimentacion/types';
import { usePets } from '../../context/PetsContext';
import {
  Alimento,
  AlimentoByMascota,
  AlimentoReq,
  UpdateAlimento,
  getEmptyAlimento,
} from '../../types/Alimentacion-Pesaje/types';
import { today } from '../../utils/date';
import { useEnter } from '../Utils/useEnter';
import {
  useDeleteAlimento,
  useGetAlimentos,
  useGetAlimentosByMascota,
  usePostAlimento,
  useUpdateAlimento,
} from './useAlimentacionPesaje';

type ModalType = 'marca' | 'alimento' | null;

export const useAlimentacionHelper = ({
  view,
  setView,
  showAlert,
  hideAlert,
}: AlimentacionProps) => {
  // ========== Context & Hooks ==========
  const { currentPet } = usePets();
  const { ref: enterRef, handleEnter } = useEnter();
  const { control, handleSubmit, errors, watch, setValue, reset } = useForm<Alimento>({
    defaultValues: getEmptyAlimento(),
  });

  // ========== API Hooks ==========
  const { alimentos, isValidating } = useGetAlimentos();
  const { alimentosByMascota, mutate: mutateGetAlimentosByMascota } =
    useGetAlimentosByMascota(String(currentPet?.id));
  const { postAlimento, isLoading: isPosting } = usePostAlimento();
  const { updateAlimento, isLoading: isUpdating } = useUpdateAlimento();
  const { deleteAlimento, isLoading: isDeleting } = useDeleteAlimento();

  // ========== Local State ==========
  const [modalOpen, setModalOpen] = useState<ModalType>(null);
  const [search, setSearch] = useState<string>('');
  const [editingAlimento, setEditingAlimento] = useState<AlimentoByMascota | null>(null);

  // ========== Form Values ==========
  const selectedMarca = watch('marca');
  const selectedNombreAlimento = watch('nombre');

  // ========== Computed Values ==========
  const alimentosByAnimal = useMemo(
    () =>
      alimentos?.filter(
        (a) => a.animal?.toLowerCase() === currentPet?.animal?.toLowerCase(),
      ) || [],
    [alimentos, currentPet],
  );

  const marcasFiltradas = useMemo(() => {
    if (!alimentosByAnimal || alimentosByAnimal.length === 0) return [];
    const marcasUnicas = Array.from(new Set(alimentosByAnimal.map((a) => a.marca)));
    return marcasUnicas.filter(Boolean);
  }, [alimentosByAnimal]);

  const alimentosByMarca = useMemo(
    () =>
      selectedMarca ? alimentosByAnimal.filter((a) => a.marca === selectedMarca) : [],
    [alimentosByAnimal, selectedMarca],
  );

  const presentacionesAlimento = useMemo(() => {
    if (!selectedNombreAlimento || !alimentos) return [];
    const alimento = alimentos.find((a) => a.nombre === selectedNombreAlimento);
    return alimento?.presentaciones || [];
  }, [selectedNombreAlimento, alimentos]);

  // TODO: ordenar por fecha de compra (desc).
  // Temporalmente se usa reverse() porque el backend no expone una fecha diferenciadora.
  const alimentosByMascotaSorted = alimentosByMascota
    ? [...alimentosByMascota].reverse()
    : [];

  // ========== Helper Functions ==========
  const resetFields = (fields: string[]) => {
    fields.forEach((f) => setValue(f, ''));
  };

  const getModalItems = () => {
    const items =
      modalOpen === 'marca'
        ? marcasFiltradas
            ?.filter((m) => m.toLowerCase().includes(search.toLowerCase()))
            .map((m) => ({ id: m, label: m }))
        : alimentosByMarca
            .filter((a) => a.nombre.toLowerCase().includes(search.toLowerCase()))
            .map((a) => ({ id: a.nombre, label: a.nombre }));

    return (
      items?.map(({ id, label }) => ({
        id,
        label,
        isSelected:
          modalOpen === 'marca' ? selectedMarca === id : selectedNombreAlimento === id,
      })) || []
    );
  };

  // ========== Modal Handlers ==========
  const handleSelectOption = (type: 'marca' | 'alimento', value: string) => {
    if (type === 'marca') {
      setValue('marca', value);
      resetFields(['nombre', 'peso']);
    } else {
      setValue('nombre', value);
      resetFields(['peso']);
    }
    setSearch(value);
    setModalOpen(null);
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
  };

  const handleOpenModal = (type: ModalType) => {
    setModalOpen(type);
  };

  const handleCloseModal = () => {
    setModalOpen(null);
  };

  const handleOpenAlimentoModal = () => {
    if (selectedMarca) {
      setModalOpen('alimento');
    } else {
      showAlert({
        header: 'Ups',
        message: 'Por favor selecciona una marca primero',
        buttons: [{ text: 'Entendido' }],
      });
    }
  };

  // ========== CRUD Handlers ==========
  const onSubmit = async (values: Alimento) => {
    try {
      // Modo edición
      if (editingAlimento) {
        // Solo llamar al backend si cambió la presentación
        if (Number(values.peso) === editingAlimento.total) {
          setView('historial');
          return;
        }

        const body: UpdateAlimento = {
          alimentoId: editingAlimento.alimento.id,
          mascotas: currentPet ? [currentPet.id] : [],
          kilos: Number(values.peso),
        };

        const success = await updateAlimento(body, async () => {
          await mutateGetAlimentosByMascota();
        });

        if (success) {
          showAlert({
            header: 'Alimento actualizado',
            message: 'El alimento se actualizó correctamente.',
            buttons: [
              {
                text: 'Entendido',
                handler: async () => {
                  hideAlert();
                  setView('historial');
                },
              },
            ],
          });
        }
        return;
      }

      // Modo creación
      const alimentoSeleccionado = alimentosByAnimal?.find(
        (a) => a.nombre === values.nombre,
      );

      const body: AlimentoReq = {
        mascotas: currentPet ? [currentPet.id] : [],
        alimentoId: alimentoSeleccionado!.id,
        kilos: Number(values.peso),
        fechaCompra: new Date(values.fechaCompra),
        duracion: 0,
        nombrePetShop: '',
        modalidadCompra: '',
        contactoProveedor: '',
      };

      const success = await postAlimento(body, async () => {
        await mutateGetAlimentosByMascota();
      });

      if (success) {
        showAlert({
          header: 'Alimento agregado',
          message: 'El alimento se agregó correctamente.',
          buttons: [
            {
              text: 'Entendido',
              handler: async () => {
                hideAlert();
                setView('historial');
              },
            },
          ],
        });
      }
    } catch (err) {
      const api = err.apiResponse;

      if (api?.status === 409) {
        showAlert({
          header: 'Error',
          message: 'Este alimento ya existe.',
          buttons: [{ text: 'Aceptar', handler: hideAlert }],
        });
      } else {
        showAlert({
          header: 'Error',
          message: 'Ocurrió un error inesperado.',
          buttons: [{ text: 'Aceptar', handler: hideAlert }],
        });
      }
    }
  };

  const handleUpdate = (alimento: AlimentoByMascota) => {
    setEditingAlimento(alimento);
    setView('agregar');
  };

  const handleDelete = async (id: number) => {
    try {
      const deleted = await deleteAlimento(id, async () => {
        await mutateGetAlimentosByMascota();
      });

      if (!deleted) {
        showAlert({
          header: 'Error',
          message: 'No se pudo eliminar el alimento',
          buttons: [{ text: 'Entendido', role: 'cancel' }],
        });
      }
    } catch (error) {
      console.error('Error al eliminar alimento:', error);
    }
  };

  const confirmDelete = (id: number) => {
    showAlert({
      header: 'Eliminar alimento',
      message: '¿Estás seguro que deseas eliminar este alimento?',
      buttons: [
        {
          text: 'Cancelar',
          role: 'cancel',
          handler: hideAlert,
        },
        {
          text: 'Eliminar',
          role: 'destructive',
          handler: async () => {
            hideAlert();
            await handleDelete(id);
          },
        },
      ],
    });
  };

  // ========== Effects ==========
  useEffect(() => {
    setSearch(modalOpen === 'marca' ? selectedMarca || '' : selectedNombreAlimento || '');
  }, [modalOpen, selectedMarca, selectedNombreAlimento]);

  useEffect(() => {
    if (selectedNombreAlimento && presentacionesAlimento.length === 1) {
      setValue('peso', presentacionesAlimento[0]);
    }
  }, [selectedNombreAlimento, presentacionesAlimento, setValue]);

  // Cargar datos del alimento cuando se entra en modo edición
  useEffect(() => {
    if (view === 'agregar' && editingAlimento) {
      setValue('marca', editingAlimento.alimento.marca);
      setValue('nombre', editingAlimento.alimento.nombre);
      setValue('peso', editingAlimento.total);
      const ultimaFecha = editingAlimento.detalles?.[0]?.fecha || today;
      setValue('fechaCompra', ultimaFecha);
    }
  }, [view, editingAlimento, setValue]);

  // Limpiar editingAlimento y resetear form cuando se sale del modo agregar
  useEffect(() => {
    if (view !== 'agregar') {
      if (editingAlimento) {
        setEditingAlimento(null);
      }
      reset();
    }
  }, [view, editingAlimento, reset]);

  return {
    isLoading: isPosting || isUpdating ? 'submit' : null,
    isDeleting,
    isValidating,
    modalOpen,
    search,
    editingAlimento,
    alimentosByMascota: alimentosByMascotaSorted,
    presentacionesAlimento,
    selectedMarca,
    selectedNombreAlimento,
    today,
    control,
    errors,
    handleSubmit,
    enterRef,
    handleEnter,
    handleSelectOption,
    handleSearchChange,
    handleOpenModal,
    handleCloseModal,
    handleOpenAlimentoModal,
    onSubmit,
    handleUpdate,
    confirmDelete,
    getModalItems,
    view,
    setView,
  };
};
